import { NODE_TYPE_LABEL } from '@chatsdet/types';
import { workflowToSpec, type PullWorkflow } from '@chatsdet/canary-spec';
import { topoOrderNodes } from '@chatsdet/workflow-diff';

type SeedOutput = {
  dataKey: string;
  name: string;
  description?: string;
  displayLabel: string;
  propertyId: string;
  attributes?: Record<string, unknown>;
  credentialOutput?: Record<string, unknown>;
};

export type WorkflowSummary = PullWorkflow & {
  seedOutputsJson?: SeedOutput[] | null;
};

export type WorkflowDetailResponse = {
  ok?: boolean;
  error?: string;
  workflow?: WorkflowSummary;
  targetOrgId?: string;
  targetOrgName?: string;
};

/** Environment fields the workflow-detail payload carries for `workflow get`. */
type WorkflowEnvFields = {
  defaultEnvironmentId?: string | null;
  environments?: Array<{ id: string; name: string; type: string; isDefault: boolean }>;
  eligibleEnvironmentIds?: string[];
  passByEnvironment?: Array<{
    environmentId: string;
    environmentName: string;
    environmentType: string;
    everPassed: boolean;
    passed: number;
    total: number;
    health: string;
  }>;
};

export function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max - 1) + '…';
}

export function getNodeTitle(node: PullWorkflow['nodes'][number]): string {
  const c = node.configJson ?? {};
  switch (node.nodeType) {
    case 'login':
      return (c.credentialName as string) || NODE_TYPE_LABEL.login;
    case 'navigate':
      return (c.pageTitle as string) || truncate((c.pageUrl as string) || 'Navigate', 40);
    case 'action':
      return (
        (c.customActionTitle as string) || truncate((c.actionDescription as string) || 'Action', 50)
      );
    case 'assertion':
      return truncate((c.condition as string) || 'Assertion', 50);
    case 'setup':
      return (c.setupFlowName as string) || 'Setup';
    case 'seed':
      return (c.seedWorkflowName as string) || 'Seed';
    case 'wait':
      return 'Wait';
    case 'condition':
      return truncate((c.conditionDescription as string) || 'Condition', 50);
    case 'end':
      return 'End';
    case 'api_sequence':
      return 'API Sequence';
    default:
      return node.nodeType;
  }
}

function getCleanConfig(node: PullWorkflow['nodes'][number]): Record<string, unknown> {
  const c = node.configJson ?? {};
  switch (node.nodeType) {
    case 'login':
      // Keep the credential wiring metadata, not secrets. Passwords/tokens live
      // on credential records, never in configJson.
      return pick(c, [
        'credentialName',
        'performLogin',
        'credentialSource',
        'credentialTemplateId',
        'credentialTemplateName',
        'credentialId',
        'seedCredentialRef',
      ]);
    case 'navigate':
      return {
        ...pick(c, ['pageUrl', 'pageTitle']),
        ...(c.openInNewTab ? { openInNewTab: true } : {}),
      };
    case 'action':
      return pick(c, [
        'actionDescription',
        'playbookSteps',
        'inputValues',
        'declaredOutputs',
        'capturedVariableKeys',
        'dataRequirements',
        'alwaysUseAgent',
      ]);
    case 'assertion':
      return pick(c, ['condition', 'expectedOutcome', 'strict', 'alwaysUseAgent']);
    case 'setup':
      return pick(c, ['setupFlowId', 'setupFlowName']);
    case 'seed':
      return pick(c, ['seedWorkflowId', 'seedWorkflowName', 'snapshotVersion']);
    case 'end':
      return pick(c, ['outcome']);
    case 'wait':
    case 'condition':
    case 'api_sequence':
      return c;
    default:
      return c;
  }
}

function pick(obj: Record<string, unknown>, keys: string[]): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) {
      result[key] = obj[key];
    }
  }
  return result;
}

export function formatWorkflowMarkdown(workflow: WorkflowSummary): string {
  const seedOutputs = workflow.seedOutputsJson ?? workflow.seedOutputs;
  return workflowToSpec({ ...workflow, seedOutputs });
}

export function formatWorkflowJson(workflow: WorkflowSummary): object {
  const sorted = topoOrderNodes(workflow.nodes, workflow.edges ?? []);
  const seedOutputs = workflow.seedOutputsJson;
  const env = workflow as WorkflowSummary & WorkflowEnvFields;

  return {
    workflow: {
      id: workflow.id,
      name: workflow.name,
      description: workflow.description,
      status: workflow.status,
      flowType: workflow.flowType,
      ...(seedOutputs && seedOutputs.length > 0 ? { seedOutputs } : {}),
      ...(env.defaultEnvironmentId !== undefined
        ? { defaultEnvironmentId: env.defaultEnvironmentId }
        : {}),
      ...(env.eligibleEnvironmentIds ? { eligibleEnvironmentIds: env.eligibleEnvironmentIds } : {}),
      ...(env.passByEnvironment && env.passByEnvironment.length > 0
        ? {
            passByEnvironment: env.passByEnvironment.map((p) => ({
              environment: `${p.environmentName} (${p.environmentType})`,
              everPassed: p.everPassed,
              passed: p.passed,
              total: p.total,
              health: p.health,
            })),
          }
        : {}),
    },
    steps: sorted.map((node, i) => ({
      index: i + 1,
      nodeId: node.id,
      nodeType: node.nodeType,
      title: getNodeTitle(node),
      config: getCleanConfig(node),
    })),
  };
}
